import React, { useEffect, useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useUI } from '../contexts/UIContext';
import API_BASE_URL from '../config/api';
import LoadingAnimation from '../components/LoadingAnimation';
import DemoBanner from '../components/DemoBanner';
import Sidebar from '../components/Sidebar';
import AIAnalyst from '../components/AIAnalyst';
import Settings from '../components/Settings';
import { preloadPortfolioOptions, loadMarketValueChange } from '../features/reports/lib/portfolioOptions';
import { preloadVariableRateBonds } from '../features/manual/lib/variableRateBonds';

const MainLayout = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { aiAnalystOpen, closeAIAnalyst, modals, closeModal } = useUI();
  const [demoMode, setDemoMode] = useState(false);

  // Ask the backend whether it is serving synthetic demo data. The backend is
  // the authority: the banner must never be missing over demo data.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const token = localStorage.getItem('access_token')
      || sessionStorage.getItem('access_token')
      || '';
    fetch(`${API_BASE_URL}/api/system/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!cancelled && body?.demo_mode) setDemoMode(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // As soon as the user is in the authenticated app, warm the report picker
  // options (and the backend market-value aggregate behind them) so the report
  // dropdown is instant and already ranked by market value. Also warm the
  // per-account market-value change feed (Portfolios day-over-day arrows) and
  // the firm-wide variable-rate bonds list (Manual view). These are cheap
  // firm-wide aggregates (one query each), NOT per-client fan-outs.
  //
  // NOTE: the pinned-family portfolio-summary preload (preloadPortfolioSummaries,
  // ~50-60 Athena queries across PRELOAD_PORTFOLIO_CODENAMES) was REMOVED here on
  // purpose: it backlogged the Athena in-flight limiter the instant a user logged
  // in, so a report generated right after login queued behind the preload wave.
  // Detail summaries now load on first open of a portfolio (read-through cache in
  // fetchPortfolioSummary), which is where the cost actually belongs. The backend
  // top-5 detail warm is likewise gated off via DETAIL_WARM_ENABLED=false.
  useEffect(() => {
    if (isAuthenticated) {
      preloadPortfolioOptions();
      loadMarketValueChange().catch(() => {});
      preloadVariableRateBonds();
    }
  }, [isAuthenticated]);

  if (isLoading) {
    return <LoadingAnimation />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className={`h-screen flex flex-col ${darkMode ? 'dark bg-[#0A0A0A]' : 'bg-white'}`}>
      <DemoBanner visible={demoMode} />

      <div className="flex-1 flex overflow-hidden">
      <Sidebar />

      {/* Main Content Area — AIAnalyst renders here when open, just like any other page */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {aiAnalystOpen
          ? <AIAnalyst
              isOpen={aiAnalystOpen}
              onClose={closeAIAnalyst}
              darkMode={darkMode}
              onToggleDarkMode={toggleDarkMode}
            />
          : (children ?? <Outlet />)
        }
      </div>
      </div>

      <Settings
        isOpen={modals.settings}
        onClose={() => closeModal('settings')}
        darkMode={darkMode}
        onToggleDarkMode={toggleDarkMode}
      />
    </div>
  );
};

export default MainLayout;
